import { Draft } from '~/composer/composer/entities/Draft';
import { AttachmentTypes, MediaTypes } from '~/constants';
import ValidationSuccess from '~/composer/composer/lib/validation/ValidationSuccess';
import ValidationFail from '~/composer/composer/lib/validation/ValidationFail';
import ValidationResult from '~/composer/composer/lib/validation/ValidationResult';
import ValidationResults from '~/composer/composer/lib/validation/ValidationResults';
import {
  getMissingAttachmentAndTextMessage,
  getMissingAttachmentMessage,
  REQUIRES_TEXT,
  REQUIRES_TEXT_OR_ATTACHMENT,
} from './utils/validationErrorMessages';
import { validateVideo } from './ValidateVideo';

export default class BaseValidator {
  protected isDraft: boolean;

  protected draft: Draft;

  protected activeThreadId: number;

  constructor(draft: Draft, isDraft: boolean, activeThreadId = 0) {
    this.draft = draft;
    this.isDraft = isDraft;
    this.activeThreadId = activeThreadId;
  }

  /**
   * Get copy for the missing attachment, used later to construct the message
   * @private
   */
  protected getRequiredAttachmentCopy(): string | null {
    const canHaveImageOrGifAttachment =
      this.draft.service?.canHaveSomeMediaAttachmentTypes([
        MediaTypes.IMAGE,
        MediaTypes.GIF,
      ]);

    const canHaveVideoAttachment =
      this.draft.service?.canHaveMediaAttachmentType(MediaTypes.VIDEO);

    if (this.draft.service?.requiredAttachmentType === AttachmentTypes.LINK) {
      return 'a link preview';
    }

    if (
      this.draft.service?.requiredAttachmentType === AttachmentTypes.RETWEET
    ) {
      return 'a retweet';
    }

    if (this.draft.service?.requiredAttachmentType === AttachmentTypes.MEDIA) {
      if (this.draft.isReelsPost()) {
        return 'a video';
      }
      if (canHaveImageOrGifAttachment && canHaveVideoAttachment) {
        return 'an image or video';
      }
      if (canHaveImageOrGifAttachment) {
        return 'an image';
      }
      if (canHaveVideoAttachment) {
        return 'a video';
      }
    }

    return null;
  }

  protected hasRequiredAttachmentAttached(): boolean {
    const requiredAttachmentType = this.draft.service?.requiredAttachmentType;

    return (
      requiredAttachmentType === null ||
      (requiredAttachmentType === AttachmentTypes.LINK &&
        this.draft.hasLinkAttachment()) ||
      (requiredAttachmentType === AttachmentTypes.RETWEET &&
        this.draft.hasRetweetAttachment()) ||
      (requiredAttachmentType === AttachmentTypes.MEDIA &&
        this.draft.hasAttachment())
    );
  }

  protected hasRequiredText(): boolean {
    return !this.draft.service?.requiresText || this.draft.hasText();
  }

  protected validateAttachmentOrText(): ValidationResult {
    if (!this.hasRequiredAttachmentAttached() && !this.hasRequiredText()) {
      return new ValidationFail(
        getMissingAttachmentAndTextMessage(
          this.getRequiredAttachmentCopy() as string
        )
      );
    }
    if (!this.hasRequiredAttachmentAttached()) {
      return new ValidationFail(
        getMissingAttachmentMessage(this.getRequiredAttachmentCopy() as string)
      );
    }
    if (!this.hasRequiredText()) {
      return new ValidationFail(REQUIRES_TEXT);
    }
    if (!this.draft.hasText() && !this.draft.hasAttachment()) {
      return new ValidationFail(REQUIRES_TEXT_OR_ATTACHMENT);
    }

    return new ValidationSuccess();
  }

  protected validateVideo(): ValidationResult {
    return validateVideo(this.draft.video, this.draft.service);
  }

  protected shouldValidateVideo(): boolean {
    return (
      !!this.draft.service?.videoMinResolution ||
      !!this.draft.service?.videoMaxSize ||
      !!this.draft.service?.videoMinDurationMs ||
      !!this.draft.service?.videoMaxDurationMs ||
      !!this.draft.service?.videoMaxAspectRatio ||
      !!this.draft.service?.videoMinAspectRatio
    );
  }

  protected validateImage(): ValidationResult {
    const { imageMaxWidth, imageMaxHeight } = this.draft.service;

    for (let i = 0; i < this.draft.images.length; i++) {
      const imageWidth = this.draft.images[i].width ?? 0;
      const imageHeight = this.draft.images[i].height ?? 0;

      if (
        imageMaxWidth &&
        imageMaxHeight &&
        (imageWidth > imageMaxWidth || imageHeight > imageMaxHeight)
      ) {
        return new ValidationFail(
          `One of your images is above maximum supported width or height: ${imageMaxWidth}x${imageMaxHeight}px`
        );
      }

      if (imageMaxWidth && imageWidth > imageMaxWidth) {
        return new ValidationFail(
          `One of your images is above maximum supported width: ${imageMaxWidth}`
        );
      }

      if (imageMaxHeight && imageHeight > imageMaxHeight) {
        return new ValidationFail(
          `One of your images is above maximum supported height: ${imageMaxHeight}`
        );
      }
    }

    return new ValidationSuccess();
  }

  protected shouldValidateImage(): boolean {
    return (
      !!this.draft.service?.imageMaxWidth ||
      !!this.draft.service?.imageMaxHeight
    );
  }

  /**
   * This method is supposed to be overriden in every Validator extending after BaseValidator
   * @protected
   */
  protected validateForChannel(): ValidationResult[] {
    return [];
  }

  /**
   * Validate the actual draft, that is when isDraft == true
   * @protected
   */
  protected validateDraft(): ValidationResult[] {
    if (
      (this.draft.hasText() || this.draft.hasAttachment()) &&
      !this.shouldValidateVideo()
    ) {
      return [];
    }

    const videoValidationResults = this.shouldValidateVideo()
      ? this.validateVideo()
      : new ValidationSuccess();

    const imageValidationResults = this.shouldValidateImage()
      ? this.validateImage()
      : new ValidationSuccess();

    return [
      this.validateAttachmentOrText(),
      videoValidationResults,
      imageValidationResults,
    ].filter((message) => typeof message !== typeof ValidationFail);
  }

  /**
   * Validate the actual update, that is when isDraft == false
   * @protected
   */
  protected validateUpdate(): ValidationResult[] {
    const videoValidationResults = this.shouldValidateVideo()
      ? this.validateVideo()
      : new ValidationSuccess();
    const imageValidationResults = this.shouldValidateImage()
      ? this.validateImage()
      : new ValidationSuccess();

    return [
      this.validateAttachmentOrText(),
      videoValidationResults,
      imageValidationResults,
      ...this.validateForChannel(),
    ].filter((message) => typeof message !== typeof ValidationFail);
  }

  public validate(): ValidationResults {
    if (this.isDraft) {
      return new ValidationResults(this.validateDraft());
    }

    return new ValidationResults(this.validateUpdate());
  }
}
